import { DatePipe } from '@angular/common';
import {Component, HostBinding, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';

import { Subject, Subscription } from 'rxjs';
import { ConfirmPopupComponent } from 'src/app/components';
import { ReportPageInterface } from 'src/app/interface';
import { FieldValue, IncomeStatement, IncomeStatementRequest, Report } from 'src/app/model';
import { EntityType, FinancialType, FooterButtonOption, IncomeStatementType, PageNumbers } from 'src/app/model/Enums';
import { ReportProvider } from 'src/app/providers';
import {CommonService, LoaderService, ReportService, ToasterService} from 'src/app/services';
import { AddInterimPopupComponent, IstExcelImportComponent, IstExcelPasteComponent, ISTHelpPupupComponent, LowValueWarningPopupComponent, HowForecastingWorksPopupComponent, IncomeInitWarningPopupComponent } from '../..';
import { radioGroupOption } from '../../commonModel/common-classes';
import { InterimRevenueWarningPopupComponent } from '../../components/interim-revenue-warning-popup/interim-revenue-warning-popup.component';
import { IstActionContextMenuComponent } from '../../components/ist-action-context-menu/ist-action-context-menu.component';
import { ReportPageConstant } from 'src/app/constants/reportPage.constants';
import {routeTransition} from '../../../../animations/route-transition';

@Component({
  selector: 'app-income',
  templateUrl: './income.component.html',
  styleUrls: ['./income.component.scss'],
  animations: [routeTransition],
})
export class IncomeComponent implements OnInit, OnDestroy, ReportPageInterface {
  @HostBinding('@routeTransition') routeAnimation = true;
  @ViewChild('incomeForm') incomeForm: NgForm;

  @ViewChild('revenueContext') revenueAction: IstActionContextMenuComponent;
  @ViewChild('profitContext') profitContext: IstActionContextMenuComponent;
  @ViewChild('interestContext') interestContext: IstActionContextMenuComponent;
  @ViewChild('depreciationContext') depreciationContext: IstActionContextMenuComponent;
  @ViewChild('amortizationContext') amortizationContext: IstActionContextMenuComponent;
  @ViewChild('officerCompContext') officerCompContext: IstActionContextMenuComponent;
  @ViewChild('guaranteedPaymentsContext') guaranteedPaymentsContext: IstActionContextMenuComponent;

  public currentReport: Report;
  private reportSub: Subscription;
  reportPageConst = ReportPageConstant;

  fyeMaxDate: string = '';
  toDayDate: string = '';
  minDate: string = '';
  minDateForRecentFinancials: string = '';

  //#region "income-table"
  validateSubscription: Subscription;
  revenueInterimWarningSubscription: Subscription;
  statementType: any[] = [];
  monthList: any[] = [];
  incomeTable: IncomeStatement[] = [];
  hasInterim: boolean = false;

  financialTypeEnum = FinancialType;
  isTypeEnum = IncomeStatementType;
  percentDialogOpen: boolean = false;

  isForm1065: boolean = false;

  forPasteData: Array<{
    value: number,
    rowIndex: number,
    is: IncomeStatement
  }> = [];

  refreshRevenue: boolean = true;
  refreshDate: boolean = true;
  disabledRecentFinancialDate: boolean = false;

  lowValueNoticeShown: boolean = false;
  lowValueNoticeVisible: boolean = false;
  isWarningForecastingShown: boolean = false;
  //#endregion

  indicationOption: radioGroupOption[] = [];
  actionListOptionShow: boolean = true;

  constructor(
    public reportService: ReportService,
    private reportProvider: ReportProvider,
    private router: Router,
    private datePipe: DatePipe,
    private toastr: ToasterService,
    private modalService: BsModalService,
    private loaderService: LoaderService,
    public common: CommonService,
  ) {
    this.toDayDate = this.datePipe.transform(new Date().setDate(new Date().getDate() + 1), 'yyyy-M-d');
    this.fyeMaxDate = (new Date().getFullYear() + 1).toString() + '-01-01';
    this.minDate = this.datePipe.transform(new Date().setFullYear(new Date().getFullYear() - 1), 'yyyy-M-d');
  }


  ngOnInit(): void {

    //#region "income-table"
    this.statementType.push({ key: 'Not Available', value: FinancialType.NOT_AVAILABLE });
    this.statementType.push({ key: 'Internal', value: FinancialType.INTERNAL });
    this.statementType.push({ key: 'Federal Tax Returns', value: FinancialType.FEDERAL_TAX_RETURNS });
    this.statementType.push({ key: 'Accountant Compiled', value: FinancialType.ACCOUNTANT_COMPILED });
    this.statementType.push({ key: 'Accountant Reviewed', value: FinancialType.ACCOUNTANT_REVIEWED });
    this.statementType.push({ key: 'Audited', value: FinancialType.AUDITED });

    for (let i = 12; i >= 0; i--) {
      this.monthList.push({
        value: i,
        key: i + (i === 1 ? ' Month' : ' Months')
      });
    }

    this.validateSubscription = this.reportService.refreshIncomeStatementRevenue.subscribe(() => {
      this.refreshRevenueFields();
    });

    this.revenueInterimWarningSubscription = this.reportService.incomeShowInterimWarning.subscribe((fieldName: string) => {
      if (this.currentReport.LATEST.ASSISTED_INTERIM_CALCULATION) {
        const it = this.incomeTable.find(el => el.IS_TYPE == IncomeStatementType.INTERIM);
        this.showRevenueInterimWarning(it, fieldName);
      }
    });
    //#endregion

    this.indicationOption.push({ title: 'Yes', value: true });
    this.indicationOption.push({ title: 'No', value: false });

    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      this.currentReport = currentReport;

      //#region "fiscal-year-end"
      if (this.currentReport?.LATEST && !this.currentReport.LATEST.FYE_DATE) {
        this.currentReport.LATEST.FYE_DATE = (new Date().getFullYear()).toString() + '-12-31';
        this.callUpdateVersionDetailsAPI('FYE_DATE');
      }
      //#endregion

      //#region "recent-financials"
      if (this.currentReport?.LATEST?.FYE_DATE) {
        let dateFormate = new Date(this.currentReport.LATEST.FYE_DATE);
        dateFormate.setDate(dateFormate.getDate() - 1);
        this.minDateForRecentFinancials = this.datePipe.transform(dateFormate, 'yyyy-M-d');
      }
      //#endregion

      //#region "income-table"
      if (this.currentReport && this.currentReport.LATEST) {
        this.isForm1065 = this.currentReport.LATEST.COMPANY.ENTITY_TYPE == EntityType.FORM_1065;
        if (this.currentReport.LATEST.INCOME_STATEMENT.length) {
          this.currentReport.LATEST.INCOME_STATEMENT.forEach(element => {
            if (element.IS_TYPE != IncomeStatementType.FORECASTED && element.IS_TYPE != IncomeStatementType.INTERIM) {
              this.incomeTable.push(element);
            }
          });
          this.incomeTable.sort((a, b) => Number(a.YEAR) - Number(b.YEAR));

          // add INTERIM year
          if (this.currentReport.LATEST.INCOME_STATEMENT.find(el => el.IS_TYPE == IncomeStatementType.INTERIM)) {
            this.hasInterim = true;
            this.incomeTable.push(this.currentReport.LATEST.INCOME_STATEMENT.find(el => el.IS_TYPE == IncomeStatementType.INTERIM));
          } else {
            this.hasInterim = false;
          }

          // add FORECASTED year
          if (this.currentReport.LATEST.INCOME_STATEMENT.find(el => el.IS_TYPE == IncomeStatementType.FORECASTED)) {
            this.incomeTable.push(this.currentReport.LATEST.INCOME_STATEMENT.find(el => el.IS_TYPE == IncomeStatementType.FORECASTED));
          }
        }
      }
      //#endregion
    });
  }

  ngOnDestroy(): void {
    if (this.currentReport?.LATEST?.INCOME_STATEMENT?.length && this.incomeTable) {
      this.currentReport.LATEST.INCOME_STATEMENT = this.incomeTable;
    }
    if (this.validateSubscription) {
      this.validateSubscription.unsubscribe();
    }
    if (this.revenueInterimWarningSubscription) {
      this.revenueInterimWarningSubscription.unsubscribe();
    }
    if (this.reportSub)
      this.reportSub.unsubscribe();
  }

  /**
   * @description field update API
   */
  callUpdateVersionDetailsAPI(fieldName: string) {
    if (fieldName == 'FYE_DATE') {
      this.disabledRecentFinancialDate = true;
    }
    let objFieldValue = new FieldValue();
    objFieldValue.GROUP = 'REPORT_VERSION';
    objFieldValue.FIELD = fieldName;
    objFieldValue.VALUE = this.currentReport.LATEST[fieldName];
    this.reportProvider.updateReportField(this.currentReport.LATEST.ID, objFieldValue).subscribe(res => {
      if (res.success) {
        if (fieldName == 'FYE_DATE') {
          this.refreshDate = false;
          setTimeout(() => {
            this.refreshDate = true;
          }, 0);
          setTimeout(() => {
            this.disabledRecentFinancialDate = false;
          }, 500);
        }
      } else {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  /**
   * @description field update API
   * @param fieldName field name which we want to update
   * @param obj income statement which year we want to update
   */
  callUpdateAPI(fieldName: string, obj: IncomeStatement) {
    if (fieldName == 'FINANCIAL_TYPE') {
      this.refreshRevenueFields();
    }
    let objFieldValue = new FieldValue();
    objFieldValue.GROUP = 'INCOME_STATEMENT';
    objFieldValue.FIELD = fieldName;
    objFieldValue.VALUE = obj[fieldName];
    this.reportProvider.updateReportField(obj.ID, objFieldValue).subscribe(res => {
      if (res.success) {
       /* if (fieldName === 'REVENUE' && obj.IS_TYPE == IncomeStatementType.INTERIM && res.result) {
          this.isInterimRevenueFocus = true;
        }*/
      } else {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  //#region "recent-financials"

  /**
   * @description for update/create income statements
   */
  changeRecentFinDate() {
    let confirmModalRef: BsModalRef;
    confirmModalRef = this.modalService.show(AddInterimPopupComponent, Object.assign({
      initialState: {
        title: 'Change Interim Date',
        message: 'You are about to Change Interim Date of Income Statements.',
        primaryButtonTitle: 'Change'
      }
    }, { class: 'modal-lg modal-dialog-centered' }));

    const ackSubject = new Subject<boolean>();
    confirmModalRef.content.subject = ackSubject;

    ackSubject.subscribe(value => {
      if (value) {
        this.callUpdateVersionDetailsAPI('RECENT_FINANCIAL_DATE');
      }
    });
  }

  /**
   * @description for create income statements
   */
  saveIncomeStatementInit() {
    this.toastr.info('We are preparing income table, please wait a moment.');
    let objSave = new IncomeStatementRequest();
    objSave.FYE_DATE = this.currentReport.LATEST.FYE_DATE;
    objSave.RECENT_FINANCIAL_DATE = this.currentReport.LATEST.RECENT_FINANCIAL_DATE;
    this.reportProvider.saveIncomeStatementInit(this.currentReport.LATEST.ID, objSave).subscribe(resp => {
      if (resp.success) {
        this.getInocomeStatement();
      }
    }, err => {
      // Error Handling
      this.showIncomeInitWarning();
    });
  }

  //#endregion

  //#region "income-table"
  refreshRevenueFields() {
    this.refreshRevenue = false;
    setTimeout(() => {
      this.refreshRevenue = true;
    }, 0);
  }

  /**
   * @description for get total of column
   * @param obj income statement object
   * @returns total of column
   */
  calculateTotal(obj: IncomeStatement) {
    if (!obj) {
      return 0;
    } else {
    return Number(obj.PROFIT ? obj.PROFIT.toString().replace(/,/g, '') : 0) +
      Number(obj.INTEREST ? obj.INTEREST.toString().replace(/,/g, '') : 0) +
      Number(obj.DEPRECIATION ? obj.DEPRECIATION.toString().replace(/,/g, '') : 0) +
      Number(obj.AMORTIZATION ? obj.AMORTIZATION.toString().replace(/,/g, '') : 0) +
      Number(obj.OFFICER_COMP ? obj.OFFICER_COMP.toString().replace(/,/g, '') : 0) +
      (this.isForm1065 ? Number(obj.GUARANTEED_PAYMENTS ? obj.GUARANTEED_PAYMENTS.toString().replace(/,/g, '') : 0) : 0);
    }
  }

  /**
   * @description for reset income statement
   */
  resetIncomeStatement() {
    let confirmModalRef: BsModalRef;
    confirmModalRef = this.modalService.show(ConfirmPopupComponent, Object.assign({
      initialState: {
        message: 'Do you want to reset income statement?'
      }
    }, { class: 'modal-sm modal-dialog-centered' }));

    const ackSubject = new Subject<boolean>();
    confirmModalRef.content.subject = ackSubject;

    ackSubject.subscribe(value => {
      if (value) {
        this.reportProvider.resetIncomeStatement(this.currentReport.LATEST.ID).subscribe(resp => {
          if (resp.success) {
            this.currentReport.LATEST.INCOME_STATEMENT = [];
            this.incomeTable = [];
            this.currentReport.LATEST.FYE_DATE = null;
            this.currentReport.LATEST.RECENT_FINANCIAL_DATE = null;
            this.currentReport.LATEST.ASSISTED_INTERIM_CALCULATION = true;
            this.currentReport.LATEST.INTERIM_FUTURE_INDICATION = null;
            if (this.currentReport?.LATEST?.LOCATION_LIST?.length) {
              this.currentReport.LATEST.LOCATION_LIST.forEach(l => {
                l.RENT_EXPENSE_EACH_YEAR = null;
                l.RENT_ADJUSTMENT_YEARLY = [];
                l.RENT_EXPENSE = null;
              });
            }
            this.lowValueNoticeShown = false;
            this.reportService.updateMaxStage(PageNumbers.INCOME, true);

            if (this.currentReport?.LATEST && !this.currentReport.LATEST.FYE_DATE) {
              this.currentReport.LATEST.FYE_DATE = (new Date().getFullYear()).toString() + '-12-31';
              this.callUpdateVersionDetailsAPI('FYE_DATE');
            }
          }
        }, err => {
          // Error Handling
        });
      }
    });

  }

  /**
   * @description for remove interim year
   */
  removeInterimClick() {
    let confirmModalRef: BsModalRef;
    confirmModalRef = this.modalService.show(ConfirmPopupComponent, Object.assign({
      initialState: {
        message: 'Do you want to remove Interim?'
      }
    }, { class: 'modal-sm modal-dialog-centered' }));

    const ackSubject = new Subject<boolean>();
    confirmModalRef.content.subject = ackSubject;

    ackSubject.subscribe(value => {
      if (value) {
        this.reportProvider.removeInterim(this.currentReport.LATEST.ID).subscribe(resp => {
          if (resp.success) {
            this.getInocomeStatement();
          } else {
            this.toastr.error(resp.error.txt);
          }
        }, err => {
          // Error Handling
        });
      }
    });
  }

  /**
   * @description for add interim year
   */
  addInterimClick() {
    let confirmModalRef: BsModalRef;
    confirmModalRef = this.modalService.show(AddInterimPopupComponent, Object.assign({
      initialState: {
        title: 'Add Interim',
        message: 'You are about to add Interim Date to Income Statements.',
        primaryButtonTitle: 'Add'
      }
    }, { class: 'modal-lg modal-dialog-centered' }));

    const ackSubject = new Subject<boolean>();
    confirmModalRef.content.subject = ackSubject;

    ackSubject.subscribe(value => {
      if (value) {
        let objSave = new IncomeStatementRequest();
        objSave.FYE_DATE = this.currentReport.LATEST.FYE_DATE;
        objSave.RECENT_FINANCIAL_DATE = this.currentReport.LATEST.RECENT_FINANCIAL_DATE;
        this.reportProvider.addInterim(this.currentReport.LATEST.ID, objSave).subscribe(resp => {
          if (resp.success) {
            this.getInocomeStatement();
          }
        }, err => {
          // Error Handling
        });
      }
    });
  }

  /**
   * @description get income statement details
   */
  getInocomeStatement() {
    this.reportProvider.getIncomeStatementList(this.currentReport.LATEST.ID).subscribe(resp => {
      if (resp.success) {
        this.currentReport.LATEST.INCOME_STATEMENT = [];
        this.incomeTable = [];
        resp.result.forEach(element => {
          element.REVENUE = element.REVENUE == 0 ? null : element.REVENUE;
          if (element.IS_TYPE != IncomeStatementType.FORECASTED && element.IS_TYPE != IncomeStatementType.INTERIM) {
            this.incomeTable.push(element);
          }
        });
        this.incomeTable.sort((a, b) => Number(a.YEAR) - Number(b.YEAR));

        // add INTERIM year
        if (resp.result.find(el => el.IS_TYPE == IncomeStatementType.INTERIM)) {
          this.hasInterim = true;
          this.incomeTable.push(resp.result.find(el => el.IS_TYPE == IncomeStatementType.INTERIM));
        } else {
          this.hasInterim = false;
        }

        // add FORECASTED year
        if (resp.result.find(el => el.IS_TYPE == IncomeStatementType.FORECASTED)) {
          this.incomeTable.push(resp.result.find(el => el.IS_TYPE == IncomeStatementType.FORECASTED));
        }

        this.currentReport.LATEST.INCOME_STATEMENT = this.incomeTable;

        setTimeout(() => {
          this.refreshRevenueFields();
        }, 10);

      }
    }, err => {
      // Error Handling
    });
  }

  /**
   * @description for change auto/manual
   */
  changeAutomaticallyManually() {
    this.currentReport.LATEST.ASSISTED_INTERIM_CALCULATION = !this.currentReport.LATEST.ASSISTED_INTERIM_CALCULATION;
    let objFieldValue = new FieldValue();
    objFieldValue.GROUP = 'REPORT_VERSION';
    objFieldValue.FIELD = 'ASSISTED_INTERIM_CALCULATION';
    objFieldValue.VALUE = this.currentReport.LATEST.ASSISTED_INTERIM_CALCULATION.toString();
    this.reportProvider.updateReportField(this.currentReport.LATEST.ID, objFieldValue).subscribe(res => {
      if (!res.success) {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  /**
   * @description for open import excel popup
   */
  importExcel() {
    if (this.currentReport.LATEST.INCOME_STATEMENT.length && !this.currentReport.LATEST.INCOME_STATEMENT.some(el => !el.FINANCIAL_TYPE)) {
      let confirmModalRef: BsModalRef;
      confirmModalRef = this.modalService.show(IstExcelImportComponent, Object.assign({
        initialState: {
          versionId: this.currentReport.LATEST.ID
        }
      }, { class: 'modal-lg modal-dialog-centered' }));

      const ackSubject = new Subject<boolean>();
      confirmModalRef.content.subject = ackSubject;

      ackSubject.subscribe(value => {
        if (value) {
          this.getInocomeStatement();
        }
      });
    } else {
      this.toastr.info("Please select Type of financials first!")
    }
  }

  /**
   * @description for get last histotical revenue
   * @returns last historical revenue
   */
  getLastHistoricalRevenue() {
    const interimYear = this.incomeTable.filter(el => el.IS_TYPE == IncomeStatementType.HISTORICAL && el.FINANCIAL_TYPE && el.FINANCIAL_TYPE != FinancialType.NOT_AVAILABLE);
    return interimYear[interimYear.length - 1] ? interimYear[interimYear.length - 1].REVENUE : 0;
  }

  /**
   * @description for get last histotical year month
   * @returns last historical month
   */
  getLastHistoricalMonth() {
    const interimYear = this.incomeTable.filter(el => el.IS_TYPE == IncomeStatementType.HISTORICAL && el.FINANCIAL_TYPE && el.FINANCIAL_TYPE != FinancialType.NOT_AVAILABLE);
    return interimYear[interimYear.length - 1] ? interimYear[interimYear.length - 1].MONTHS : 1;
  }

  /**
   * @description for return seleted month
   * @param is income statement object
   * @returns return selected month
   */
  getSelectedMonth(is: IncomeStatement) {
    return is.MONTHS || 1;
  }

  /**
   * @description for open paste excel popup
   */
  pasteExcel() {
    if (this.currentReport.LATEST.INCOME_STATEMENT.length && !this.currentReport.LATEST.INCOME_STATEMENT.some(el => !el.FINANCIAL_TYPE)) {
      let confirmModalRef: BsModalRef;
      confirmModalRef = this.modalService.show(IstExcelPasteComponent, Object.assign({
        initialState: {
          versionId: this.currentReport.LATEST.ID
        }
      }, { class: 'modal-lg modal-dialog-centered' }));

      const ackSubject = new Subject<string>();
      confirmModalRef.content.subject = ackSubject;

      ackSubject.subscribe((data: string) => {
        if (data) {
          this.parseAndSetData(data);
        }
      });
    } else {
      this.toastr.info("Please select Type of financials first!");
    }
  }

  /**
   * @description for parse and set data of pasted data
   * @param data paste data
   */
  private parseAndSetData(data: string): void {
    this.forPasteData = [];
    let rows = data.split('\n');
    rows = rows.filter(el => { // remove blank object/row
      return el != ''
    });
    if (rows && rows.length && rows[0].toString().includes('$')) { // check valid excel
      for (let r = 0; r < rows.length; r++) {
        let cols = rows[r].split('\t');
        let colindex = 0;

        for (let i = 0; i < this.incomeTable.length; i++) {
          // Paste for only Available IS
          if (IncomeStatement.isAvailable(this.incomeTable[i])) {
            if (cols[colindex]) { // check column value
              if (this.incomeTable[i].IS_TYPE != IncomeStatementType.FORECASTED) {
                let cellVal = cols[colindex].replace(/,/g, '')
                  .replace(',', '')
                  .replace(/\$/g, '')
                  .replace(/\s/g, '');

                if (cellVal.length > 8) {
                  this.toastr.error('values should be 8 digits or less and meet the template design');
                  return;
                }
                if (Number(cellVal)) {
                  this.forPasteData.push(
                    {
                      value: Math.ceil(Number(cellVal)),
                      rowIndex: r,
                      is: this.incomeTable[i]
                    }
                  );
                } else {
                  this.forPasteData.push(
                    {
                      value: 0,
                      rowIndex: r,
                      is: this.incomeTable[i]
                    }
                  );
                }
              }
            } else {
              this.forPasteData.push(
                {
                  value: 0,
                  rowIndex: r,
                  is: this.incomeTable[i]
                }
              );
            }
            if (r === rows.length - 1 && i === this.incomeTable.length - 1) {
              this.setPasteValue();
            }

            colindex++;
          }
        }
      }
    } else {
      this.toastr.info("Please copy valid data!");
    }
  }

  /**
   * @description for set paste value
   */
   async setPasteValue() {
    const loadingObj = {
      loading: true,
      loadingMessage: 'Please wait...'
    };
    this.loaderService.isLoading.next(loadingObj);
    for (let i = 0; i < this.forPasteData.length; i++) {
      const element = this.forPasteData[i];
      let fieldName = '';
      switch (element.rowIndex) {
        case 0: // REVENUE
          element.is.REVENUE = element.value;
          fieldName = 'REVENUE';
          break;
        case 1: // PROFIT
          element.is.PROFIT = element.value;
          fieldName = 'PROFIT';
          break;
        case 2: // INTEREST
          element.is.INTEREST = element.value;
          fieldName = 'INTEREST';
          break;
        case 3: // DEPRECIATION
          element.is.DEPRECIATION = element.value;
          fieldName = 'DEPRECIATION';
          break;
        case 4: // AMORTIZATION
          element.is.AMORTIZATION = element.value;
          fieldName = 'AMORTIZATION';
          break;
        case 5: // OFFICER_COMP
          element.is.OFFICER_COMP = element.value;
          fieldName = 'OFFICER_COMP';
          break;
        case 6: // GUARANTEED_PAYMENTS
          element.is.GUARANTEED_PAYMENTS = element.value;
          fieldName = 'GUARANTEED_PAYMENTS';
          break;
      }
      if (fieldName) // check field name
        await this.callUpdateAPISequential(fieldName, element.is).then(res => {
          if (i == this.forPasteData.length - 1) {
            const loadingObj2 = {
              loading: false,
              loadingMessage: ''
            };
            this.loaderService.isLoading.next(loadingObj2);
          }
        });
    }

  }

  callUpdateAPISequential(fieldName: string, obj: IncomeStatement) {
    return new Promise<void>(resolve => {
      let objFieldValue = new FieldValue();
      objFieldValue.GROUP = 'INCOME_STATEMENT';
      objFieldValue.FIELD = fieldName;
      objFieldValue.VALUE = obj[fieldName];
      this.reportProvider.updateReportField(obj.ID, objFieldValue).subscribe(res => {
        if (res.success) {
         /* if (fieldName === 'REVENUE' && obj.IS_TYPE == IncomeStatementType.INTERIM && res.result) {
            this.isInterimRevenueFocus = true;
          }*/
        }
        resolve();
      }, err => {
        // Error Handling
      });
    });
  }

  /**
   * @description for open help popup
   */
  openHelpModal() {
    this.modalService.show(ISTHelpPupupComponent, Object.assign({
      initialState: {
        defaultOpenOption: this.currentReport.LATEST.COMPANY.ENTITY_TYPE
      }
    }, { class: 'modal-lg help-modal' }));
  }

  /**
   * @description for get diff percentage
   * @returns diff percentage
   */
  getDifferencePercentage() {
    let diffPercentage = 0;
    const historicalYears = this.incomeTable.filter(el => el.IS_TYPE == IncomeStatementType.HISTORICAL && el.FINANCIAL_TYPE && el.FINANCIAL_TYPE != FinancialType.NOT_AVAILABLE);
    const forecastedYear = this.incomeTable.filter(el => el.IS_TYPE == IncomeStatementType.FORECASTED);

    if (historicalYears.length && forecastedYear) {

      let forecastedMargin = this.getMargin(this.calculateTotal(forecastedYear[0]), forecastedYear[0].REVENUE);
      if (forecastedMargin === null)
        return 0;

      const lastHistoricalYear = historicalYears[historicalYears.length - 1];
      let historicalMargin = this.getMargin(this.calculateTotal(lastHistoricalYear), lastHistoricalYear.REVENUE);


      diffPercentage = Number(forecastedMargin) - Number(historicalMargin);

    }
    return this.reportService.round(diffPercentage || 0, 1);
  }

  /**
   * @description for margin calculation
   * @param base base value
   * @param revenue revenue value
   * @returns margin
   */
  private getMargin(base: number, revenue: number = 0): number {
    revenue = Number(revenue ? revenue.toString().replace(/,/g, '') : 0);
    if (revenue == 0) {
      return this.reportService.round(0, 1);
    }
    return this.reportService.round(Number(base) / Number(revenue) * 100, 1);
  }

  /**
   * @description for check Not Available financial type, if 2 or more years are NA at that time hide H%
   * @returns check NA
   */
  checkNotAvailableFinType(): boolean {
    const notAvailableYear = this.incomeTable.filter(el => el.FINANCIAL_TYPE == 'NOT_AVAILABLE');
    if (notAvailableYear.length < 2) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * @description for scroll focuse on inputs
   * @param $event key down
   */
  @HostListener('window:keyup', ['$event'])
  onKeyPress($event: KeyboardEvent) {
    if ($event.keyCode == 9) {
      const elePosition = document.activeElement.getBoundingClientRect();
      if (window.innerHeight - 115 < elePosition.y) {
        document.activeElement.scrollIntoView(true);
      }
    }
  }

  /**
   * @description check validation for FINANCIAL_TYPE is seleted or not
   * @returns return false when every FINANCIAL_TYPE is NA
   */
  checkHistoricalYears(): boolean {
    if (this.incomeTable.findIndex(el => el.FINANCIAL_TYPE && el.IS_TYPE == IncomeStatementType.HISTORICAL && el.FINANCIAL_TYPE != this.financialTypeEnum.NOT_AVAILABLE) > -1) {
      return true;
    } else {
      this.toastr.error('At least one year should be selected as available.');
      return false;
    }
  }

  checkOne12Months(): boolean {
    if (this.incomeTable.some(el => el.FINANCIAL_TYPE && el.IS_TYPE == IncomeStatementType.HISTORICAL && el.FINANCIAL_TYPE != this.financialTypeEnum.NOT_AVAILABLE && el.MONTHS == 12)) {
      return true;
    } else {
      this.toastr.error('At least 1 year should be 12 months.');
      return false;
    }
  }

  checkLessthen12Months(): boolean {
    if (this.incomeTable.filter(el => el.FINANCIAL_TYPE && el.IS_TYPE == IncomeStatementType.HISTORICAL && el.FINANCIAL_TYPE != this.financialTypeEnum.NOT_AVAILABLE && el.MONTHS < 12).length < 2) {
      return true;
    } else {
      this.toastr.error('There can’t be more than 1 year which is less than 12 months.');
      return false;
    }
  }

  showHowForecastingWorks(): void {
    this.modalService.show(HowForecastingWorksPopupComponent, { class: 'modal-lg modal-dialog-centered' });
  }

  showRevenueWarning(it: IncomeStatement): void {
    if (it.IS_TYPE == IncomeStatementType.INTERIM) {
      // ***** this popup open from event emitter ***** //
    } else {
      this.showLowValueWarning(it.REVENUE);
    }
  }

  showLowValueWarning(revenue: number): void {
    let rev = Number(revenue);
    const isSkipLowValueWarning = localStorage.getItem('skipLowValueWarning_' + this.currentReport.ID);

    if (rev < 9999 && rev != 0 && !this.lowValueNoticeShown && isSkipLowValueWarning !== 'true') {
      this.lowValueNoticeVisible = true;
      let lowValueWarningModalRef: BsModalRef;
      lowValueWarningModalRef = this.modalService.show(LowValueWarningPopupComponent, { class: 'modal-lg modal-dialog-centered' });
      const confirmSubject = new Subject<boolean>();
      lowValueWarningModalRef.content.subject = confirmSubject;
      confirmSubject.subscribe(() => {
        this.lowValueNoticeShown = true;
        this.lowValueNoticeVisible = false;
      });
    }
  }

  showRevenueInterimWarning(it: IncomeStatement, fieldName: string) {
    const forecastedYears = this.incomeTable.filter(el => el.IS_TYPE == IncomeStatementType.FORECASTED);
    const fieldValueDiv = this.getLastHistoricalRevenue() / this.getLastHistoricalMonth();
    const fieldDiv = forecastedYears[0]?.[fieldName] / forecastedYears[0]?.MONTHS;
    let historicalMargin = this.reportService.round(((Number(fieldDiv) * 100) / Number(fieldValueDiv)) - 100, 1);
    const isSkipInterimWarning = localStorage.getItem('skipInterimWarning_' + this.currentReport.ID);

    setTimeout(() => {
      if (Math.abs(historicalMargin) > 200 && !this.isWarningForecastingShown && !this.lowValueNoticeVisible && isSkipInterimWarning !== 'true') {
        this.isWarningForecastingShown = true;
        let WarningForecastingModalRef: BsModalRef;
        WarningForecastingModalRef = this.modalService.show(InterimRevenueWarningPopupComponent, Object.assign({
          initialState: {
            year: it.YEAR,
            percent: historicalMargin,
            fieldName: IncomeStatement.getFieldFullName(fieldName)
          }
        }, {class: 'modal-lg modal-dialog-centered'}));
        const confirmSubject = new Subject<boolean>();
        WarningForecastingModalRef.content.subject = confirmSubject;
        confirmSubject.subscribe(() => {
          this.isWarningForecastingShown = false;
        });
      }
    }, 5);
  }

  //#endregion

  /**
   * @description function of interface for validation
   * @returns form valid or not
   */
  isFormValid(): boolean {
    return !this.incomeForm.invalid && this.currentReport.LATEST.INCOME_STATEMENT.length > 1 && this.checkHistoricalYears() && this.checkOne12Months() && this.checkLessthen12Months();
  }

  /**
   * @description function of interface for skip functionality
   * @returns form can be skippable or not
   */
  canBeSkipped(): boolean {
    return false;
  }

  /**
   * @description report footer button click options
   * @param buttonOption which button clicked by user
   */
  footerButtonClick(buttonOption: number) {
    if (buttonOption == FooterButtonOption.NEXT_CLICK && !this.percentDialogOpen) {
      if (this.currentReport.LATEST.INCOME_STATEMENT.length) {
        if (this.checkHistoricalYears() && this.checkOne12Months() && this.checkLessthen12Months()) {
          this.reportService.updateMaxStage(PageNumbers.INCOME);
          this.router.navigateByUrl(`/report/${this.currentReport.ID}/future-projection`);
        }
      } else {
        this.saveIncomeStatementInit(); // for create income statement
      }
    } else if (buttonOption == FooterButtonOption.BACK_CLICK) {
      this.router.navigateByUrl(`/report/${this.currentReport.ID}/ownership`);
    } else if (buttonOption == FooterButtonOption.EXTRA_CLICK) { // for reset income table
      this.resetIncomeStatement();
    }
  }

  /** Show Warning on Init API fail */
  showIncomeInitWarning() {
      let incomeInitWarningModalRef: BsModalRef;
      incomeInitWarningModalRef = this.modalService.show(IncomeInitWarningPopupComponent, { class: 'modal-lg modal-dialog-centered' });
      const confirmSubject = new Subject<boolean>();
      incomeInitWarningModalRef.content.subject = confirmSubject;
      confirmSubject.subscribe((val) => {
        if(val) {
          window.location.reload();
        }
      });
  }
}
